import React from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';

const KanbanColumn = ({ title, status, inquiries, onStatusChange, onCardClick, colorClass }) => {
    return (
        <div className="flex flex-col bg-gray-50 rounded-xl p-4 min-w-[300px] max-w-[350px] shrink-0 h-full border border-gray-200">
            <div className={`flex items-center justify-between mb-4 pb-2 border-b-2 ${colorClass}`}>
                <h3 className="font-bold text-gray-800">{title}</h3>
                <span className="bg-white text-gray-500 text-xs font-bold px-2 py-1 rounded-full shadow-sm">{inquiries.length}</span>
            </div>
            
            <div className="flex flex-col gap-3 overflow-y-auto pb-4 h-[600px] scrollbar-thin">
                {inquiries.map(inq => (
                    <Card 
                        key={inq.id} 
                        className="cursor-pointer hover:shadow-md hover:border-primary-300 transition-all active:scale-95 bg-white scale-100"
                        onClick={() => onCardClick(inq.id)}
                    >
                        <CardContent className="p-4">
                            <div className="flex justify-between items-start mb-2">
                                <Badge variant={
                                    inq.type === 'ESTIMATE' || inq.type === 'SELFQUOTE' ? 'primary' :
                                    inq.type === 'DEMO' ? 'success' : 'neutral'
                                }>
                                    {inq.type}
                                </Badge>
                                <span className="text-xs text-gray-400">#{inq.id}</span>
                            </div>
                            
                            <h4 className="font-bold text-gray-900 mt-1 mb-1 truncate text-sm">
                                {inq.companyName || inq.customerName || '이름 없음'}
                            </h4>
                            
                            <p className="text-sm text-gray-600 line-clamp-2 my-2 min-h-[40px]">
                                {inq.message || '내용 없음'}
                            </p>
                            
                            <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                                <span className="text-xs text-gray-400">
                                    {new Date(inq.createdAt).toLocaleDateString()}
                                </span>
                                
                                {/* Simple Dropdown for fast state transition */}
                                <select 
                                    className="text-xs border border-gray-200 rounded p-1"
                                    onClick={e => e.stopPropagation()}
                                    value={inq.status}
                                    onChange={e => onStatusChange(inq.id, e.target.value)}
                                >
                                    <option value="UNREAD">읽지 않음</option>
                                    <option value="READ">확인중</option>
                                    <option value="RESPONDED">답변/진행중</option>
                                    <option value="DONE">종료</option>
                                    <option value="REJECTED">거절</option>
                                </select>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
};

const InquiryKanban = ({ proposals, handleStatusChange, setSelectedInquiryId }) => {
    // Group inquiries by status
    const grouped = {
        UNREAD: proposals.filter(p => p.status === 'UNREAD'),
        READ: proposals.filter(p => p.status === 'READ'),
        RESPONDED: proposals.filter(p => p.status === 'RESPONDED'),
        DONE: proposals.filter(p => p.status === 'DONE'),
        REJECTED: proposals.filter(p => p.status === 'REJECTED'),
    };

    return (
        <div className="flex gap-6 overflow-x-auto pb-8 pt-4">
            <KanbanColumn 
                title="신규 문의 (UNREAD)" 
                status="UNREAD" 
                inquiries={grouped.UNREAD} 
                onStatusChange={handleStatusChange}
                onCardClick={setSelectedInquiryId}
                colorClass="border-semantic-danger"
            />
            <KanbanColumn 
                title="내용 확인 (READ)" 
                status="READ" 
                inquiries={grouped.READ} 
                onStatusChange={handleStatusChange}
                onCardClick={setSelectedInquiryId}
                colorClass="border-semantic-warning"
            />
            <KanbanColumn 
                title="진행 중 (RESPONDED)" 
                status="RESPONDED" 
                inquiries={grouped.RESPONDED} 
                onStatusChange={handleStatusChange}
                onCardClick={setSelectedInquiryId}
                colorClass="border-primary-500"
            />
            <KanbanColumn 
                title="딜 클로징 (DONE)" 
                status="DONE" 
                inquiries={grouped.DONE} 
                onStatusChange={handleStatusChange}
                onCardClick={setSelectedInquiryId}
                colorClass="border-semantic-success"
            />
            {grouped.REJECTED.length > 0 && (
                <KanbanColumn 
                    title="거절됨 (REJECTED)" 
                    status="REJECTED" 
                    inquiries={grouped.REJECTED} 
                    onStatusChange={handleStatusChange}
                    onCardClick={setSelectedInquiryId}
                    colorClass="border-gray-400"
                />
            )}
        </div>
    );
};

export default InquiryKanban;
